import React from "react";
import { useNavigate } from "react-router-dom";

import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { ClaimGridItemDetailResponse, ClaimSearchGridResponse } from "@/main/models/response";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";

import ThreeDotsClaim from "./threeDotsClaim";
import * as S from "./styles";
interface IHeaderCollapseSearchClaimProps {
	dataGrid: ClaimSearchGridResponse;
	dataDetailClaim: ClaimGridItemDetailResponse;
	isLoadingDetailClaim: boolean;
	refreshClaim: () => void;
	refreshClaimCounts: () => void;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}
function HeaderCollapseSearchClaim({
	dataGrid,
	dataDetailClaim,
	refreshClaim,
	refreshClaimCounts,
	setIsLoading,
}: IHeaderCollapseSearchClaimProps) {
	const ID_SCREEN = "id-screen-claim";
	const navigate = useNavigate();

	return (
		<S.Container>
			<ColumnLabel
				label={getTranslateByKey("information.claimTotal")}
				content={formatCurrency(dataDetailClaim.totalPrice)}
			/>
			<S.AreaQuotesStatus>
				{dataDetailClaim.countByStatus.map((status, indexStatus) => (
					<S.AreaCollumnLabelStatus
						key={indexStatus}
						$isDelayed={status.key === getTranslateByKey("information.delayed")}
					>
						<ColumnLabel key={status.key} label={status.key} content={status.value} />
					</S.AreaCollumnLabelStatus>
				))}
			</S.AreaQuotesStatus>
			<ThreeDotsClaim.Root idScreen={ID_SCREEN}>
				<ThreeDotsClaim.OptionHistory quotes={dataDetailClaim.quotes} procurements={dataDetailClaim.procurements} />
				<ThreeDotsClaim.OptionAudit guidClaim={dataGrid.guidClaim} hasProcurement={dataDetailClaim.hasProcurement} />
				<ThreeDotsClaim.OptionCancel
					showCancelButton={dataDetailClaim.showCancelButton}
					guidClaim={dataGrid.guidClaim}
					refreshClaim={refreshClaim}
				/>
				<ThreeDotsClaim.OptionReturn
					showReturnButton={dataDetailClaim.showReturnButton}
					guidClaim={dataGrid.guidClaim}
					refreshClaim={refreshClaim}
				/>
				<ThreeDotsClaim.OptionImage guidClaim={dataGrid.guidClaim} isQuoteCanceled />
				<ThreeDotsClaim.OptionTracking
					guidClaim={dataGrid.guidClaim}
					hasAwaitingPayment={dataDetailClaim.hasAwaitingPayment}
					hasDeliveryBySupplier={dataDetailClaim.hasDeliveryBySupplier}
					hasProcurement={dataDetailClaim.hasProcurement}
				/>
				<ThreeDotsClaim.OptionRequote
					showRequoteButton={dataDetailClaim.showRequoteButton}
					guidClaim={dataGrid.guidClaim}
					refreshClaim={refreshClaim}
				/>

				<ThreeDotsClaim.OptionGenerateProcurement
					showGenerateButton={dataDetailClaim.hasManualReviewPending}
					guidClaim={dataGrid.guidClaim}
					claimNumber={String(dataGrid.claimNumber)}
					guidBillingAddress={dataDetailClaim.guidBillingAddress}
					refreshClaim={refreshClaim}
					refreshClaimCounts={refreshClaimCounts}
					setIsLoading={setIsLoading}
				/>

				<ThreeDotsClaim.OptionDirectAssign
					guidClaim={dataGrid.guidClaim}
					isQuoteManual={dataDetailClaim.hasQuoteManual}
					isPossibleTotalLoss={dataDetailClaim.possibleTotalLoss}
					guidCompanyBuyer={dataDetailClaim.guidCompanyBuyer}
					onSuccess={(willGenerateOrderAutomatically) => {
						if (willGenerateOrderAutomatically) {
							refreshClaim();
							return;
						}

						navigate(
							`${RoutesConstants.claimSearchClaims}${getRefreshForceUrl({
								claimNumber: String(dataGrid.claimNumber),
							})}`,
						);
					}}
				/>
				<ThreeDotsClaim.ClaimReassignPartSearchClaim
					guidClaim={String(dataGrid.guidClaim)}
					idStatus={dataGrid.idClaimStatus}
					canShow={dataDetailClaim.showReassignAll}
				/>
				<ThreeDotsClaim.OptionsSendUpdates
					guidClaim={String(dataGrid.guidClaim)}
					showSendUpdatesButton={dataDetailClaim.showSendUpdateButton}
				/>
				<ThreeDotsClaim.OptionPrintSupplierPartsReport guidClaim={dataGrid.guidClaim} />
			</ThreeDotsClaim.Root>
		</S.Container>
	);
}

type IWrapperHeaderCollapseSearchClaimProps = Omit<IHeaderCollapseSearchClaimProps, "dataDetailClaim"> & {
	dataDetailClaim: ClaimGridItemDetailResponse | null;
};

function WrapperHeaderCollapseSearchClaim(props: IWrapperHeaderCollapseSearchClaimProps) {
	if (props.isLoadingDetailClaim) return <LoadingComponent />;
	if (isNullUndefined(props.dataDetailClaim)) return <ResultNotFoundComponent />;

	return <HeaderCollapseSearchClaim {...(props as IHeaderCollapseSearchClaimProps)} />;
}

export default WrapperHeaderCollapseSearchClaim;
